import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import { appSyncApiConfig as config } from "./config"
import { QueryResponse } from "shared/types/queryResponse"
// import type { DocumentVersion } from "../../shared/types-exp"

// Define a service using a base URL and expected endpoints
export const documentVersionsApi = createApi({
  reducerPath: "documentVersionsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.baseUrl,
    prepareHeaders: (headers) => {
      headers.set("x-api-key", config.apiKey)

      return headers
    },
  }),
  tagTypes: ["DocumentVersions"],
  endpoints: (builder) => ({
    createDocumentVersion: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation CreateDocumentVersion( 
              $documentId: ID = "",
              $isProject: Boolean = false,
              $isCountry: Boolean = false,
              $isGlobal: Boolean = false,
              $documentRefId: String = "",
              $latestVersion: Int = 1, 
              $refId: String = "",
              $access: [String] = [],
              $sections: [ChapterSectionInput] = [], 
              $users: [String] = "", 
            ) {
              createDocumentVersion(
                input: {
                  isProject: $isProject,
                  isCountry: $isCountry,
                  isGlobal: $isGlobal,
                  refId: $refId,
                  documentRefId: $documentRefId
                  documentId: $documentId, 
                  latestVersion: $latestVersion, 
                  sections: $sections,
                  access: $access,
                  users: $users, 
                }
              ) {
                isGlobal
                isCountry
                isProject
                documentRefId
                documentId
                id
                refId
                latestVersion
                access
                sections {
                  type
                  id
                  refId
                  isProject
                  isCountry
                  isClientEditable
                  isGlobal
                  name
                  viewing
                  isProject
                }
                users
              }
            }
          `,
          variables: {
            documentId: data.documentId,
            latestVersion: data.latestVersion,
            isGlobal: data.isGlobal,
            isCountry: data.isCountry,
            isProject: data.isProject,
            refId: data.refId,
            documentRefId: data.documentRefId,
            users: data.users,
            sections: data.sections,
            access: data.access,
          },
        },
        providesTags: ["DocumentVersions"],
      }),
    }),
    updateDocumentVersion: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation UpdateDocumentVersion(
              $documentId: ID = ""
              $id: ID = ""
              $latestVersion: Int = 1
              $access: [String] = []
              $sections: [ChapterSectionInput] = []
              $users: [String] = ""
            ) {
              updateDocumentVersion(
                input: {
                  documentId: $documentId
                  id: $id
                  latestVersion: $latestVersion
                  users: $users
                  access: $access
                  sections: $sections
                }
              ) {
                documentId
                isGlobal
                isCountry
                isProject
                refId
                documentRefId
                id
                latestVersion
                access
                sections {
                  id
                  refId
                  isProject
                  isCountry
                  isClientEditable
                  isGlobal
                  name
                  viewing
                  type
                }
                users
              }
            }
          `,
          variables: {
            id: data.id,
            documentId: data.documentId,
            latestVersion: data.latestVersion,
            isGlobal: data.isGlobal,
            isCountry: data.isCountry,
            isProject: data.isProject,
            documentRefId: data.documentRefId,
            users: data.users,
            access: data.access,
            sections: data.sections,
          },
        },
      }),
      invalidatesTags: ["DocumentVersions"],
    }),
    batchUpdateDocumentVersions: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation BatchUpdateDocumentVersions($objects: [UpdateDocumentVersionInput!]!) {
              batchUpdateDocumentVersions(input: {objects: $objects}) {
                id
                documentId
              }
            }
          `,
          variables: {
            objects: data.objects,
          },
        },
      }),
      invalidatesTags: ["DocumentVersions"],
    }),
    // ===================================== V1 queries =====================================
    GetDocumentVersionWithChaptersV1: builder.query<
      QueryResponse<"getDocumentVersion">,
      { documentId: string; id: string }
    >({
      keepUnusedDataFor: 0,
      query: (params) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query GetDocumentVersionWithChaptersV1($documentId: ID = "", $id: ID = "") {
              getDocumentVersion(documentId: $documentId, id: $id) {
                documentId
                id
                isGlobal
                isCountry
                isProject
                refId
                documentRefId
                chapters {
                   items {
                          id
                          name
                          refId
                          access
                          sharedId
                          parentId
                          isGlobal
                          isProject
                          isCountry
                          treeIndex
                          chapterOrder
                          documentRefId
                          documentVersionId
                          assignedProjectUser
                          isIncludedInWorkflow
                          subchapterCreateAllowed
                          editing {
                            name
                            email
                            surname
                            isEditing
                          }
                          epi_status {
                            status
                            lastUpdated
                            userLastUpdated
                          }
                          client_status {
                            status
                            lastUpdated
                            userLastUpdated
                          }
                          sections {
                            content {
                              plainText
                              uploadedFiles {
                                id
                                name
                                size
                                type
                                content
                              }
                            }
                            id
                            name
                            type
                            refId
                            viewing
                            isProject
                            isCountry
                            isGlobal
                            isClientEditable
                          }
                      }
                }
                access
                sections {
                  content {
                    plainText
                    uploadedFiles {
                      id
                      name
                      size
                      type
                      content
                    }
                  }
                  id
                  refId
                  isProject
                  isCountry
                  isClientEditable
                  isGlobal
                  name
                  viewing
                  type
                }
              }
            }
          `,
          variables: {
            documentId: params.documentId,
            id: params.id,
          },
        },
      }),
      providesTags: ["DocumentVersions"],
    }),
    GetDocumentVersionAccessRightsV1: builder.query<
      QueryResponse<"getDocumentVersion">,
      { documentId: string; id: string }
    >({
      query: (params) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query GetDocumentVersionAccessRightsV1($documentId: ID = "", $id: ID = "") {
              getDocumentVersion(documentId: $documentId, id: $id) {
                documentId
                id
                access
              }
            }
          `,
          variables: {
            documentId: params.documentId,
            id: params.id,
          },
        },
      }),
      providesTags: ["DocumentVersions"],
    }),
    GetDocumentVersionForManageSectionV1: builder.query<
      QueryResponse<"getDocumentVersion">,
      { documentId: string; id: string }
    >({
      query: (params) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query GetDocumentVersionAccessRightsV1($documentId: ID = "", $id: ID = "") {
              getDocumentVersion(documentId: $documentId, id: $id) {
                id
                documentId
                refId
                sections {
                  id
                  refId
                  isProject
                  isCountry
                  isClientEditable
                  isGlobal
                  name
                  viewing
                  type
                }
              }
            }
          `,
          variables: {
            documentId: params.documentId,
            id: params.id,
          },
        },
      }),
      providesTags: ["DocumentVersions"],
    }),
    GetFullDocumentVersionWithChaptersV1: builder.query<
      QueryResponse<"getDocumentVersion">,
      { documentId: string; id: string }
    >({
      keepUnusedDataFor: 0,
      query: (params) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query GetFullDocumentVersionWithChaptersV1($documentId: ID = "", $id: ID = "") {
              getDocumentVersion(documentId: $documentId, id: $id) {
                documentId
                isGlobal
                isCountry
                isProject
                refId
                documentRefId
                id
                latestVersion
                access
                sections {
                  content {
                    plainText
                    uploadedFiles {
                      id
                      name
                      size
                      type
                      content
                    }
                  }
                  id
                  refId
                  isProject
                  isCountry
                  isClientEditable
                  isGlobal
                  name
                  type
                  viewing
                }
                users
                chapters {
                   items {
                      id
                      name
                      refId
                      access
                      sharedId
                      parentId
                      isGlobal
                      isProject
                      isCountry
                      treeIndex
                      chapterOrder
                      documentRefId
                      documentVersionId
                      assignedProjectUser
                      isIncludedInWorkflow
                      subchapterCreateAllowed
                      editing {
                        name
                        email
                        surname
                        isEditing
                      }
                      epi_status {
                        status
                        lastUpdated
                        userLastUpdated
                      }
                      client_status {
                        status
                        lastUpdated
                        userLastUpdated
                      }
                      sections {
                        content {
                          plainText
                          uploadedFiles {
                            id
                            name
                            size
                            type
                            content
                          }
                        }
                        id
                        name
                        type
                        refId
                        viewing
                        isProject
                        isCountry
                        isGlobal
                        isClientEditable
                      }
                  }
                }
              }
            }
          `,
          variables: {
            documentId: params.documentId,
            id: params.id,
          },
        },
      }),
      providesTags: ["DocumentVersions"],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useCreateDocumentVersionMutation,
  useUpdateDocumentVersionMutation,
  useBatchUpdateDocumentVersionsMutation,
  // ================ V1 ================
  useGetDocumentVersionWithChaptersV1Query,
  useGetDocumentVersionAccessRightsV1Query,
  useGetDocumentVersionForManageSectionV1Query,
  useGetFullDocumentVersionWithChaptersV1Query,
  util: documentVersionsUtil,
} = documentVersionsApi
